import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, ArrowLeft, CheckCircle, AlertTriangle, PenTool, ArrowRight, Shield, FileAudio, Mail, User, Building2 } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { siteConfig, signNowConfig } from "../data/mock";

const proOptions = [
  { value: "", label: "Select PRO Affiliation" },
  { value: "BMI", label: "BMI" },
  { value: "ASCAP", label: "ASCAP" },
  { value: "SESAC", label: "SESAC" },
  { value: "PRS", label: "PRS" },
  { value: "SOCAN", label: "SOCAN" },
  { value: "GEMA", label: "GEMA" },
  { value: "None", label: "Not Registered with a PRO" },
];

const liabilityCheckboxes = [
  {
    id: "ownership",
    label: "Ownership",
    text: "I confirm that I control 100% of the Master Recording and Publishing rights for the submitted works."
  },
  {
    id: "clearance",
    label: "Clearance",
    text: "I explicitly warrant that these works contain ZERO uncleared samples, unauthorized loops, or copyright-infringing material."
  },
  {
    id: "human",
    label: "Human-Authored",
    text: "I confirm that these works are 100% human-produced and contain no AI-generated audio or vocal cloning."
  }
];

const IngestionPage = () => {
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);

  const [formData, setFormData] = useState({
    producerName: "",
    email: "",
    proAffiliation: "",
    fileLink: "",
    ownership: false,
    clearance: false,
    human: false,
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const isFormValid = () => {
    return (
      formData.producerName.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.email.includes("@") &&
      formData.proAffiliation !== "" &&
      formData.fileLink.trim() !== "" &&
      formData.ownership &&
      formData.clearance &&
      formData.human
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;
    
    setIsSubmitting(true);
    
    // Simulate submission delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // In production, you would send this to your backend or email service
    console.log("Ingestion Form Submitted:", formData);
    
    setIsSubmitting(false);
    setSubmitted(true);
  };

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] relative overflow-hidden">
      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      
      {/* Glow orbs */}
      <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-[rgba(218,255,1,0.04)] rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-[rgba(218,255,1,0.03)] rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative z-10">
        
        {/* Back Link */}
        <motion.a
          href="/"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs sm:text-sm hover:text-[rgb(218,255,1)] transition-colors mb-10 sm:mb-12"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <ArrowLeft size={16} />
          Back to Home
        </motion.a>

        <AnimatePresence mode="wait">
          {!submitted ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Header */}
              <div className="mb-10 sm:mb-12">
                <Badge
                  variant="outline"
                  className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] sm:text-xs tracking-[0.2em] rounded-md"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  ASSET INGESTION PORTAL
                </Badge>
                
                <h1 
                  className="text-white text-2xl sm:text-3xl md:text-4xl font-bold mb-4"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  Submit Catalog for <span className="text-[rgb(218,255,1)]">Sync Evaluation</span>
                </h1>
                
                <p 
                  className="text-[rgb(161,161,170)] text-sm sm:text-base leading-relaxed"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Complete the form below to submit your catalog for A&R review. All submissions must meet our clearance requirements.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Producer Name */}
                <div>
                  <label 
                    htmlFor="producerName"
                    className="flex items-center gap-2 text-white text-xs sm:text-sm font-medium mb-2"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <User size={14} className="text-[rgb(218,255,1)]" />
                    Producer / Entity Name
                  </label>
                  <input
                    type="text"
                    id="producerName"
                    name="producerName"
                    value={formData.producerName}
                    onChange={handleInputChange}
                    placeholder="e.g., D-Workz Productions"
                    className="w-full bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-xl px-4 py-3 text-white text-sm placeholder:text-[rgb(100,100,110)] focus:outline-none focus:border-[rgb(218,255,1)] focus:ring-1 focus:ring-[rgb(218,255,1)] transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label 
                    htmlFor="email"
                    className="flex items-center gap-2 text-white text-xs sm:text-sm font-medium mb-2"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <Mail size={14} className="text-[rgb(218,255,1)]" />
                    Contact Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="producer@email.com"
                    className="w-full bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-xl px-4 py-3 text-white text-sm placeholder:text-[rgb(100,100,110)] focus:outline-none focus:border-[rgb(218,255,1)] focus:ring-1 focus:ring-[rgb(218,255,1)] transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    required
                  />
                </div>

                {/* PRO Affiliation */}
                <div>
                  <label 
                    htmlFor="proAffiliation"
                    className="flex items-center gap-2 text-white text-xs sm:text-sm font-medium mb-2"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <Building2 size={14} className="text-[rgb(218,255,1)]" />
                    PRO Affiliation
                  </label>
                  <select
                    id="proAffiliation"
                    name="proAffiliation"
                    value={formData.proAffiliation}
                    onChange={handleInputChange}
                    className="w-full bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[rgb(218,255,1)] focus:ring-1 focus:ring-[rgb(218,255,1)] transition-colors appearance-none cursor-pointer"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    required
                  >
                    {proOptions.map(option => (
                      <option key={option.value} value={option.value} className="bg-[rgb(26,28,30)]">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Secure File Link */}
                <div>
                  <label 
                    htmlFor="fileLink"
                    className="flex items-center gap-2 text-white text-xs sm:text-sm font-medium mb-2"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <FileAudio size={14} className="text-[rgb(218,255,1)]" />
                    Secure File Link
                  </label>
                  <input
                    type="url"
                    id="fileLink"
                    name="fileLink"
                    value={formData.fileLink}
                    onChange={handleInputChange}
                    placeholder="https://dropbox.com/... or https://drive.google.com/..."
                    className="w-full bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-xl px-4 py-3 text-white text-sm placeholder:text-[rgb(100,100,110)] focus:outline-none focus:border-[rgb(218,255,1)] focus:ring-1 focus:ring-[rgb(218,255,1)] transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    required
                  />
                  <p className="text-[rgb(100,100,110)] text-[10px] sm:text-xs mt-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Provide a private Dropbox, Google Drive, or DISCO link. WAV files required. No MP3s.
                  </p>
                </div>

                {/* Liability Shield */}
                <div className="pt-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield size={16} className="text-[rgb(218,255,1)]" />
                    <span 
                      className="text-[rgb(218,255,1)] text-[10px] sm:text-xs tracking-[0.2em] font-medium"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      LIABILITY SHIELD — MANDATORY CONFIRMATIONS
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    {liabilityCheckboxes.map((checkbox) => (
                      <label
                        key={checkbox.id}
                        className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                          formData[checkbox.id]
                            ? "bg-[rgba(218,255,1,0.05)] border-[rgb(218,255,1)]"
                            : "bg-[rgb(26,28,30)] border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          name={checkbox.id}
                          checked={formData[checkbox.id]}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                          formData[checkbox.id]
                            ? "bg-[rgb(218,255,1)] border-[rgb(218,255,1)]"
                            : "border-[rgba(255,255,255,0.3)]"
                        }`}>
                          {formData[checkbox.id] && (
                            <CheckCircle size={12} className="text-[rgb(17,17,19)]" />
                          )}
                        </div>
                        <div>
                          <span 
                            className="text-white text-sm font-semibold block mb-1"
                            style={{ fontFamily: "'Inter', sans-serif" }}
                          >
                            {checkbox.label}
                          </span>
                          <span 
                            className="text-[rgb(161,161,170)] text-xs leading-relaxed"
                            style={{ fontFamily: "'Inter', sans-serif" }}
                          >
                            {checkbox.text}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Warning if not all checked */}
                {(!formData.ownership || !formData.clearance || !formData.human) && (
                  <div className="flex items-start gap-2 p-3 bg-[rgba(239,68,68,0.1)] border border-red-500/30 rounded-lg">
                    <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-red-400 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
                      You must confirm all three liability statements before submitting.
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={!isFormValid() || isSubmitting}
                  className={`w-full py-4 rounded-xl text-sm font-semibold tracking-[0.1em] transition-all duration-300 ${
                    isFormValid()
                      ? "bg-[rgb(218,255,1)] text-[rgb(17,17,19)] hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_30px_rgba(218,255,1,0.4)]"
                      : "bg-[rgb(63,63,63)] text-[rgb(100,100,110)] cursor-not-allowed"
                  }`}
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
                      />
                      PROCESSING...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Upload size={16} />
                      SUBMIT CATALOG FOR REVIEW
                    </span>
                  )}
                </Button>
              </form>
            </motion.div>
          ) : (
            /* Success State */
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-10"
            >
              {/* Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-20 h-20 mx-auto mb-8 bg-[rgba(218,255,1,0.1)] border-2 border-[rgb(218,255,1)] rounded-full flex items-center justify-center"
              >
                <CheckCircle size={40} className="text-[rgb(218,255,1)]" />
              </motion.div>

              <Badge
                variant="outline"
                className="border-[rgb(74,222,128)] text-[rgb(74,222,128)] bg-[rgba(74,222,128,0.05)] mb-6 px-4 py-1.5 text-[10px] sm:text-xs tracking-[0.2em] rounded-md"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                ASSETS RECEIVED
              </Badge>

              <h2 
                className="text-white text-2xl sm:text-3xl font-bold mb-4"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                Catalog Submitted <span className="text-[rgb(218,255,1)]">Successfully</span>
              </h2>

              <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-xl p-6 mb-8 text-left">
                <p 
                  className="text-[rgb(218,218,218)] text-sm leading-relaxed mb-4"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  To finalize your ingestion into the ItzGreen Music evaluation pipeline, you must execute the <span className="text-[rgb(218,255,1)] font-semibold">1-Page Administration Agreement</span>.
                </p>
                <p 
                  className="text-[rgb(161,161,170)] text-xs"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Our A&R team will not review your assets until this agreement is signed.
                </p>
              </div>

              {/* SignNow CTA */}
              <a
                href={signNowConfig.signingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-sm font-semibold tracking-[0.1em] px-8 py-4 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_30px_rgba(218,255,1,0.4),0_8px_25px_rgba(218,255,1,0.3)] transition-all duration-300 w-full sm:w-auto group"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <PenTool size={18} />
                EXECUTE ADMIN AGREEMENT
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>

              <p 
                className="text-[rgb(100,100,110)] text-xs mt-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Questions? Contact <a href="mailto:leaver@itzgreenmusic.com" className="text-[rgb(218,255,1)] hover:underline">leaver@itzgreenmusic.com</a>
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-12 sm:mt-16"
        >
          <p 
            className="text-[rgb(218,255,1)] text-xs sm:text-sm font-bold tracking-[0.15em] mb-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            {siteConfig.tagline}
          </p>
          <p 
            className="text-[rgb(161,161,170)] text-[10px] sm:text-xs"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {siteConfig.copyright}
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default IngestionPage;
